import { combine, createEvent, createStore, sample } from 'effector';
import type { AssistantMessage, AssistantMarkdownMessage } from '../types/assistant';
import { userInteracted } from './appState';
import { sceneLoadingStarted, sceneLoadingFinished } from './sceneLoading';

// true once scene loading has begun (preview GLB fetch started)
const $sceneLoadingActive = createStore(false).on(
  sceneLoadingStarted,
  () => true
);

// true when reveal animation is done and preview-model is disposed
export const $sceneFullyRevealed = createStore(false).on(
  sceneLoadingFinished,
  () => true
);

// Onboarding was dismissed during the current session
export const onboardingDismissed = createEvent();
const $dismissed = createStore(false).on(onboardingDismissed, () => true);

// Dismiss on first user interaction
sample({ clock: userInteracted, target: onboardingDismissed });

// Show on any touch-capable device
const $isTouchDevice = createStore(
  'ontouchstart' in window || navigator.maxTouchPoints > 0
);

// Final visibility flag for the UI
export const $onboardingVisible = combine(
  $sceneLoadingActive,
  $dismissed,
  $isTouchDevice,
  (active, dismissed, touch) =>
    touch && active && !dismissed
);

// --- Intro flow ---

type AnyIntroMessage = AssistantMessage | AssistantMarkdownMessage;

export const introReceived = createEvent<AnyIntroMessage>();
export const introMessageReceived = createEvent<AnyIntroMessage>();

const $introData = createStore<AnyIntroMessage | null>(null).on(
  introReceived,
  (_, intro) => intro
);

const $introShown = createStore(false).on(introMessageReceived, () => true);

// Ready to show intro:
// - Touch: overlay dismissed + scene fully revealed + intro loaded
// - Non-touch: scene revealed + intro loaded
const $readyToShowIntro = combine(
  { intro: $introData, revealed: $sceneFullyRevealed, dismissed: $dismissed, isTouch: $isTouchDevice, shown: $introShown },
  ({ intro, revealed, dismissed, isTouch, shown }) => {
    if (shown || !intro) {
      return false;
    }
    if (isTouch) {
      return dismissed && revealed;
    }
    return revealed;
  }
);

sample({
  clock: $readyToShowIntro,
  source: $introData,
  filter: (intro, ready): intro is AnyIntroMessage => ready && intro !== null,
  target: introMessageReceived,
});
